import { useState, useEffect } from "react";
import { QueryFunction, useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

export enum PositionSource {
  API = "API",
  TXNS = "Txns",
}

export type Position = {
  totals?: any;
  exchange: string;
  asset: string;
  amount: number;
  totalsV2?: any;
  positionSource: string;
  setPositionSource: any;
  error: any;
};

export const usePositionsData = (
  params: {
    exchangeId?: string;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    enabled?: boolean;
  } = {}
) => {
  const [positionSource, setPositionSource] = useState<PositionSource | null>(null);

  const { getToken } = useAuth();
  const {
    exchangeId,
    staleTime = 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus = true,
    enabled = true,
  } = params || {};

  /** fetch function that react-query will use to fetch position data */
  const getPositionsData: QueryFunction<Position> = async () => {
    try {
      const token = await getToken();

      console.log("🔍 usePositionsData - Fetching from /positions with params:", exchangeId ? { exchangeId } : {});
      const res = await authorizedAxios(token).get(`/positions`, {
        params: exchangeId ? { exchangeId } : {},
      });

      console.log("🔍 usePositionsData - Raw API response:", JSON.stringify(res.data, null, 2));
      console.log("🔍 usePositionsData - res.data.result:", JSON.stringify(res.data?.result, null, 2));
      console.log("🔍 usePositionsData - res.data.balances:", JSON.stringify(res.data?.balances, null, 2));
      console.log("🔍 usePositionsData - res.data.totals:", JSON.stringify(res.data?.totals, null, 2));
      console.log("🔍 usePositionsData - res.data.totalsV2:", JSON.stringify(res.data?.totalsV2, null, 2));

      // Handle both successful response with data and error responses
      if (res.data?.error) {
        console.error("🔍 usePositionsData - Error in response:", res.data.error);
        throw new Error(res.data.error);
      }

      const returnData = res.data?.result || res.data || {};
      console.log("🔍 usePositionsData - Returning data:", returnData);
      return returnData;
    } catch (error: any) {
      // Enhanced error handling
      console.error("🔍 usePositionsData - Fetch error:", error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  };

  const query = useQuery({
    queryKey: ["positions", exchangeId],
    queryFn: getPositionsData,
    enabled,
    refetchOnWindowFocus,
    staleTime,
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  useEffect(() => {
    if (!query.data || positionSource !== null) return;

    const totalsV2 = query.data.totalsV2 || {};
    const assets = Object.values(totalsV2);

    const hasAPIData = assets.some((asset: any) => asset?.amount > 0);
    const hasTXNSData = assets.some(
      (asset: any) => asset?.calculatedAmount > 0
    );

    if (hasAPIData) {
      setPositionSource(PositionSource.API);
    } else if (hasTXNSData) {
      setPositionSource(PositionSource.TXNS);
    } else {
      setPositionSource(PositionSource.API);
    }
  }, [query.data, positionSource]);

  // Enhanced return with custom error messages
  const resolvedPositionSource = positionSource ?? PositionSource.API;

  return {
    ...query,
    positionSource: resolvedPositionSource,
    setPositionSource,
    // Add custom error handling for specific cases
    error:
      query.error || (query.data as any)?.error === "tax jurisdiction not set"
        ? new Error("Complete your Tax Settings to see Balances")
        : query.error,
  };
};
